/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useMemo, useState} from "react";
import {useIntl} from "react-intl";
import * as Yup from "yup";
import {FormikProps, useFormik} from "formik";
import {FormInput, FormInputLabel, Forms, FormSelect, GradientButton, Icon, MinValue, notify, Fa2Modal, Auth} from "@housebets/housebets-x-package";
import {CodeInput} from '@housebets/housebets-x-package/lib/components/form/CodeInput';

import apiWallet from "app/api/wallet";
import {useAppSelector} from "hooks/useAppSelector";
import {getCurrencyIcon} from "app/utils/help";
import {useGTMEvents} from "app/hooks/useGTMEvents";

import {IWithdrawCryptoFormProps, TFormikConfig, TPayload} from "./types";
import { useCryptoDropdownData } from "../../CryptoDropdown/hooks/useCryptoDropdown";
import pkg from 'lodash';
const {debounce, sortBy} = pkg;

export const WithdrawCryptoForm: React.FC<IWithdrawCryptoFormProps> = ({category, options}) => {
    const {$t} = useIntl();
    const {form} = useGTMEvents();
    const payProviderList = useAppSelector((state) => state.config.platformConfig?.payProviderList || []);
    const balance = useAppSelector((state) => state.user.balance);
    const turnoverData = useAppSelector((state) => state.user.turnoverData);
    const formSchema = (payProviderList: any[], category: any, $t: any) =>
        Yup.object().shape({
            currency: Yup.number()
                .required($t({id: "FieldIsRequired"}))
                .test("minAmount", function (value) {
                    const {network} = this.parent;
                    const minDeposit = payProviderList.find((v) => v.tokenId === category?.id && v.providerName === network);
                    const min = minDeposit?.minWithdraw || 0;
                    const {$t} = this.options.context || {};
                    const errorMsg = $t ? $t({id: "MinWithdrawError"}, {A: min}) : `Minimum withdrawal amount is ${min}`;
                    if (value === undefined || value === null) return false;
                    return value >= min || this.createError({message: errorMsg});
                }),
            twoFaCode: Yup.string()
                .test("checkIfTaken", $t({id: 'pleaseEnterValidCode'}), (value) => {
                    if (isNextStepFa2 === false) {
                        return true;
                    }

                    if (value && value?.length === 6) {
                        return true;
                    }

                    return false;
                }),
        });

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    const networkOpt = useMemo(() => {
        const channels = payProviderList.filter((x) => x.tokenId === category?.id && x.canDeposit === 1) || [];
        const res = sortBy(channels, (item) => item.listOrder)
            .map((x) => x.providerName)
            .map((v) => {
                return {
                    id: v,
                    name: v,
                };
            });
        return res || [];
    }, [category, payProviderList]);

    const initData: TFormikConfig = {
        currency: "",
        network: networkOpt[0]?.id || "",
        address: "",
        twoFaCode: "",
    };

    const [isNextStepFa2, setNextStepFa2] = useState(false);

    useEffect(() => {
        if (isNextStepFa2 === false) {
            formik.setFieldTouched("twoFaCode", false, false);
            formik.validateForm();
        }
    }, [isNextStepFa2]);

    const formik: FormikProps<TFormikConfig> = useFormik<TFormikConfig>({
        initialValues: {
            ...initData,
        },
        validationSchema: formSchema(payProviderList, category, $t),
        validateOnBlur: true,
        onSubmit: async (data) => {
            if (isLoading) return;
            const payload: TPayload = data;
            setIsLoading(true);
            const {address, currency, network, twoFaCode} = payload;

            if (isNextStepFa2 === true) {
                setNextStepFa2(false);
            }

            try {
                const {status, data} = await apiWallet.withdrawCrypto({
                    type: Number(category?.id) || 0,
                    protocol: String(network),
                    amount: Number(currency),
                    dstwalletaddr: address,
                    twoFaCode: twoFaCode ? Number(twoFaCode) : undefined,
                });

                if (data.code === -13) {
                    formik.setFieldValue("twoFaCode", "");
                    setNextStepFa2(true);
                    return;
                }

                if (status === 200 && data.code === 0) {
                    notify({
                        type: "success",
                        title: $t({id: "WithDrawSuccessTips"}, {A: `${formik.values.currency} ${category?.title}`}),
                    });
                    formik.resetForm();
                    initForm(formik);
                    form.event({
                        event: "form_submit",
                        form_id: "withdraw_crypto_form",
                        form_name: "Withdraw Crypto Form",
                    });
                    return;
                }
                form.event({
                    event: "form_error",
                    form_id: "withdraw_crypto_form",
                    form_name: "Withdraw Crypto Form",
                    additional_parameters: {
                        message: $t({id: "Error"}),
                    },
                });

                if (data.code === 7) {
                    notify({
                        type: "error",
                        title: $t({id: "WithdrawError_7"}),
                    });
                } else if (data.code === -22) {
                    notify({
                        type: "error",
                        title: $t({id: "WithdrawError_22"}),
                    });
                } else if (data.code === -1) {
                    notify({
                        type: "error",
                        title: $t({id: "WithdrawSuccessMsg"}),
                    });
                } else {
                    notify({
                        type: "error",
                        title: "error " + data?.code,
                    });
                }
            } catch (er) {
                setError($t({id: "SomeError"}));
            } finally {
                setIsLoading(false);
            }
        },
    });

    const turnoverDict = useMemo(() => {
        return turnoverData
            .map((x) => {
                return {
                    type: x.type,
                    amount: x.amount,
                    wager: x.wager,
                };
            })
            .reduce((pre, cur) => {
                pre[cur.type] = cur;
                return pre;
            }, Object.create(null));
    }, [turnoverData]);

    const minAmount = useMemo(() => {
        const network = formik.values.network + "";
        const minDeposit = payProviderList.find((v) => v.tokenId === category?.id && v.providerName === network);
        return minDeposit?.minWithdraw;
    }, [category, formik.values.network]);

    const maxAmount = useMemo(() => {
        const network = formik.values.network + "";
        const minDeposit = payProviderList.find((v) => v.tokenId === category?.id && v.providerName === network);

        const userBalance = balance.find((v) => v.type === category?.id)?.amount;
        return Math.min(minDeposit?.maxWithdraw || 0, userBalance || 0);
    }, [category, formik.values.network]);

    const initForm = (formik: FormikProps<TFormikConfig>, setError?: (v: boolean) => void) => {
        formik.setValues({...initData});
        formik.setErrors({});
        formik.setTouched({});
        setError?.(false);
    };

    useEffect(() => {
        const keys = Object.keys(formik.errors);
        keys.forEach((item) => {
            form.error({
                field: item,
                message: formik.errors[item as keyof TFormikConfig] as string,
                form_id: "withdraw_crypto_form",
                form_name: "Withdraw Crypto Form",
            });
        });
    }, [formik.errors]);

    useEffect(() => {
        initForm(formik);
    }, [category]);

    const steelNeed = useMemo(() => {
        return options?.filter(item => item?.tokenId === category?.id)[0]?.bet;
    }, [options, category]);

    return (
        <>
            <Forms.WalletForm.Wrapper title={$t({id: "WithdrawCrypto"})}>
                <form id="withdraw_crypto_form" onSubmit={formik.handleSubmit}>
                    <Forms.WalletForm.FormRow>
                        <FormInput
                            size="sm"
                            placeholder="0.00000000"
                            name="currency"
                            type="text"
                            label={<FormInputLabel blueBold>{$t({id: "ToWithdrawA"}, {A: <span>{formik.values?.network}</span>})}</FormInputLabel>}
                            errorMsgAbsolute
                            image={<Icon customIcon={getCurrencyIcon(category?.id || 0)} />}
                            value={formik.values?.currency}
                            errors={
                                (!!formik.errors?.currency && !!formik.touched?.currency) || error
                                    ? {
                                          message: formik.errors?.currency || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("currency", val)}
                            onFocus={() => formik.setFieldTouched("currency", false)}
                            onBlur={() => formik.setFieldTouched("currency", true)}
                            maxButton={{
                                text: $t({id: "Max"}),
                                onMaxClick: () => {
                                    formik.setFieldValue("currency", maxAmount);
                                },
                            }}
                        />
                    </Forms.WalletForm.FormRow>
                    <Forms.WalletForm.FormRow>
                        <FormSelect
                            size="sm"
                            options={networkOpt}
                            placeholder={$t({id: "Network"})}
                            label={$t({id: "Network"})}
                            name="network"
                            required
                            errorMsgAbsolute
                            value={formik.values?.network as string}
                            errors={
                                (!!formik.errors?.network && !!formik.touched?.network) || error
                                    ? {
                                          message: formik.errors?.network || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("network", val[0])}
                            onFocus={() => {
                                formik.setFieldTouched("network", false);
                                formik.setFieldTouched("network", false);
                                formik.setFieldTouched("network", false);
                            }}
                            onBlur={() => {
                                formik.setFieldTouched("network", true);
                                formik.setFieldTouched("network", true);
                                formik.setFieldTouched("network", true);
                            }}
                        />
                    </Forms.WalletForm.FormRow>
                    <Forms.WalletForm.FormRow>
                        <FormInput
                            size="sm"
                            placeholder={$t({id: "Address"})}
                            name="address"
                            type="text"
                            errorMsgAbsolute
                            label={$t({id: "YourPersonalWithdrawAddress"})}
                            value={formik.values?.address}
                            errors={
                                (!!formik.errors?.address && !!formik.touched?.address) || error
                                    ? {
                                          message: formik.errors?.address || error,
                                      }
                                    : null
                            }
                            onChange={(val) => formik.setFieldValue("address", val)}
                            onFocus={() => formik.setFieldTouched("address", false)}
                            onBlur={() => formik.setFieldTouched("address", true)}
                        />
                    </Forms.WalletForm.FormRow>
                    <Forms.WalletForm.FormRow>
                        {Number(turnoverDict[category?.id || 0]?.wager) < Number(turnoverDict[category?.id || 0]?.amount) && (
                            <Forms.WalletForm.FormText boldColor="#fff">
                                {$t({id: "YouStillNeedBetA"}, {A: <span>{steelNeed?.current || 0}/{steelNeed?.max || 0}</span>})}
                            </Forms.WalletForm.FormText>
                        )}
                        <Forms.WalletForm.Warning
                            v2
                            text={{
                                warning: $t({id: "Warning"}),
                                body: $t({id: "PleaseVerifyAddressBeforeTransactionW"}),
                            }}
                        />
                    </Forms.WalletForm.FormRow>
                    <Forms.WalletForm.BottomGroup>
                        <Forms.WalletForm.FormRow>
                            <MinValue currency={getCurrencyIcon(category?.id || 0)} minAmount={minAmount + ""} />
                        </Forms.WalletForm.FormRow>
                        <Forms.WalletForm.FormRow>
                            <GradientButton
                                id="withdraw_crypto_form_submit_button"
                                fullWidth
                                kind="fill"
                                type="submit"
                                isLoading={isLoading}
                                text={$t({id: "Withdraw"})}
                                disabled={!formik.isValid}
                            />
                        </Forms.WalletForm.FormRow>
                    </Forms.WalletForm.BottomGroup>
                </form>
            </Forms.WalletForm.Wrapper>

            <Fa2Modal isOpen={isNextStepFa2} onClose={() => setNextStepFa2(false)}>
                <form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                    <Auth.Form.Row>
                        <Auth.Form.FormTitle>{$t({id: "TwoFactorAuthForm.verification"})}</Auth.Form.FormTitle>
                        <Auth.Form.FormSubTitle>{$t({id: 'TwoFactorAuthForm.enterCode'})}</Auth.Form.FormSubTitle>
                    </Auth.Form.Row>
                    <Auth.Form.Row>
                        <CodeInput
                            name="twoFaCode"
                            inputMode="numeric"
                            type="number"
                            label=""
                            disabled={false}
                            fields={6}
                            value={formik.values.twoFaCode}
                            error={
                                formik?.errors?.twoFaCode
                                    ? {
                                          message: "",
                                      }
                                    : null
                            }
                            touch={() =>
                                formik.setTouched({
                                    twoFaCode: true,
                                })
                            }
                            untouch={() =>
                                formik.setTouched({
                                    twoFaCode: false,
                                })
                            }
                            onChange={(v) => {
                                formik.setFieldValue("twoFaCode", String(v));
                                formik.setFieldTouched("twoFaCode", true, false);
                                setTimeout(() => {
                                    formik.validateField("twoFaCode");
                                });
                            }}
                        />
                    </Auth.Form.Row>
                    <Forms.TFA.Row padding>
                        <Forms.TFA.Button type="submit" text={$t({id: "Withdraw"})} />
                    </Forms.TFA.Row>
                </form>
            </Fa2Modal>
        </>
    );
};

WithdrawCryptoForm.displayName = "WithdrawCryptoForm";
